import React, { useState } from 'react';
import { ArrowLeft, Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { authApi } from '../services/api';
import { theme } from '../styles/theme';

function ResetPasswordPage({ onNavigate, token }) {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Le mot de passe doit contenir au moins une minuscule, une majuscule et un chiffre';
    } else if (!/\W/.test(formData.password)) {
      newErrors.password = 'Le mot de passe doit contenir au moins un caractère spécial';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Veuillez confirmer le mot de passe';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setApiError('');

    try {
      await authApi.resetPassword(token, formData.password);
      setSuccess(true);
    } catch (error) {
      setApiError(error.message || 'Erreur lors de la réinitialisation');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className={theme.classes.page + " flex items-center justify-center p-4"}>
        <div className="max-w-md w-full text-center">
          <div className={theme.classes.card.default + " shadow-xl p-8"}>
            <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-4">
              Lien invalide
            </h1>
            <p className="text-gray-400 mb-6">
              Le lien de réinitialisation est invalide ou a expiré.
            </p>
            <button
              onClick={() => onNavigate('login')}
              className="px-6 py-2 bg-yellow-500 text-black rounded-lg
                         hover:bg-yellow-400 transition-colors font-medium"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={theme.classes.page + " flex items-center justify-center p-4"}>
        <div className="max-w-md w-full text-center">
          <div className={theme.classes.card.default + " shadow-xl p-8"}>
            <div className="bg-green-500/20 p-4 rounded-full w-fit mx-auto mb-4">
              <CheckCircle className="h-12 w-12 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">
              Mot de passe réinitialisé !
            </h1>
            <p className="text-gray-400 mb-6">
              Votre mot de passe a été modifié avec succès.
              Vous pouvez maintenant vous connecter.
            </p>
            <button
              onClick={() => onNavigate('login')}
              className="px-6 py-2 bg-yellow-500 text-black rounded-lg
                         hover:bg-yellow-400 transition-colors font-medium"
            >
              Se connecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={theme.classes.page + " flex items-center justify-center p-4"}>
      <div className="max-w-md w-full">
        <div className={theme.classes.card.default + " shadow-xl p-8"}>
          {/* Header */}
          <div className="text-center mb-8">
            <button
              onClick={() => onNavigate('login')}
              className={theme.classes.button.ghost + " mb-6 inline-flex items-center"}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à la connexion
            </button>
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{
                backgroundColor: `${theme.colors.primary.DEFAULT}33`,
                borderWidth: '1px',
                borderColor: `${theme.colors.primary.DEFAULT}4D`
              }}
            >
              <Lock className="w-8 h-8" style={{ color: theme.colors.primary.DEFAULT }} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Nouveau mot de passe
            </h1>
            <p className="text-gray-400">
              Créez un nouveau mot de passe sécurisé
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {apiError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center space-x-2 text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{apiError}</span>
              </div>
            )}

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-12 py-3 bg-black/30 border rounded-lg
                             text-white placeholder-gray-500 focus:outline-none
                             focus:ring-2 focus:ring-yellow-300 focus:border-transparent
                             transition-all ${
                    errors.password ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                  }`}
                  placeholder="Nouveau mot de passe"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-400">{errors.password}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                8 caractères minimum avec majuscule, minuscule, chiffre et caractère spécial
              </p>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-12 py-3 bg-black/30 border rounded-lg
                             text-white placeholder-gray-500 focus:outline-none
                             focus:ring-2 focus:ring-yellow-300 focus:border-transparent
                             transition-all ${
                    errors.confirmPassword ? 'border-red-500/50 bg-red-500/5' : 'border-white/[0.07]'
                  }`}
                  placeholder="Confirmer le mot de passe"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full font-semibold py-3 rounded-lg transition-all duration-200
                         disabled:bg-white/10 disabled:cursor-not-allowed"
              style={{
                backgroundColor: isSubmitting ? undefined : theme.colors.primary.DEFAULT,
                color: isSubmitting ? undefined : '#000000'
              }}
            >
              {isSubmitting ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
